(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var OPEN_CLASS = 'is-open';
  var EMPTY_CLASS = 'is-empty';

  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var sections = [];
  var cartState = null;
  var activeCurrency = null;

  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function dispatchCartUpdated(cart) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart: cart } })
      );
    } catch (err) {}
  }

  function jsonFetch(url, options) {
    var opts = options || {};
    var headers = Object.assign(
      {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      opts.headers || {}
    );
    return fetch(url, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin'
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        if (!res.ok) {
          var err = new Error(
            (data && data.description) || (data && data.message) || 'Cart request failed'
          );
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Section registry
   * ------------------------------------------------------------------ */

  function registerSection(section) {
    if (sections.indexOf(section) === -1) sections.push(section);
  }

  function unregisterSection(section) {
    var i = sections.indexOf(section);
    if (i !== -1) sections.splice(i, 1);
  }

  function getSectionIds() {
    return sections.map(function (s) { return s.id; }).filter(Boolean).join(',');
  }

  function withSections(body) {
    var payload = Object.assign({}, body || {});
    var ids = getSectionIds();
    if (ids) payload.sections = ids;
    return payload;
  }

  /* ------------------------------------------------------------------ *
   * Cart API
   * ------------------------------------------------------------------ */

  function addToCart(variantId, quantity, properties) {
    var body = {
      id: Number(variantId),
      quantity: quantity != null ? Number(quantity) : 1
    };
    if (properties && Object.keys(properties).length) {
      body.properties = properties;
    }
    return jsonFetch('/cart/add.js', {
      method: 'POST',
      body: withSections(body)
    }).then(function () {
      return refreshCart();
    });
  }

  function addMultipleToCart(items) {
    var normalized = (items || []).map(function (item) {
      var entry = {
        id: Number(item.id),
        quantity: item.quantity != null ? Number(item.quantity) : 1
      };
      if (item.properties && Object.keys(item.properties).length) {
        entry.properties = item.properties;
      }
      if (item.selling_plan) entry.selling_plan = item.selling_plan;
      return entry;
    });
    return jsonFetch('/cart/add.js', {
      method: 'POST',
      body: withSections({ items: normalized })
    }).then(function () {
      return refreshCart();
    });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      body: withSections({ id: lineItemKey, quantity: Number(quantity) })
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return jsonFetch('/cart/change.js', {
      method: 'POST',
      body: withSections({
        id: lineItemKey,
        selling_plan: Number(sellingPlanId)
      })
    }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function getCart() {
    // GET /cart.js ignores `sections`, so a no-op POST /cart/update.js is the
    // only way to get the full cart AND the re-rendered section HTML back.
    return jsonFetch('/cart/update.js', {
      method: 'POST',
      body: withSections({ updates: {} })
    });
  }

  function refreshCart() {
    return getCart().then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  /* ------------------------------------------------------------------ *
   * DOM rendering via Section Rendering API
   * ------------------------------------------------------------------ */

  function syncText(curr, next, selector) {
    var c = curr.querySelector(selector);
    var n = next.querySelector(selector);
    if (c && n) c.textContent = n.textContent;
  }

  function syncInnerHTML(curr, next, selector) {
    var c = curr.querySelector(selector);
    var n = next.querySelector(selector);
    if (c && n) c.innerHTML = n.innerHTML;
  }

  function syncCartItems(curr, next) {
    var currContainer = curr.querySelector('[data-cart-items]');
    var nextContainer = next.querySelector('[data-cart-items]');
    if (!currContainer || !nextContainer) return;

    var currByKey = {};
    Array.prototype.forEach.call(
      currContainer.querySelectorAll('[data-line-item]'),
      function (el) {
        var key = el.getAttribute('data-line-item-key');
        if (key) currByKey[key] = el;
      }
    );

    var frag = document.createDocumentFragment();
    Array.prototype.forEach.call(
      nextContainer.querySelectorAll('[data-line-item]'),
      function (nextEl) {
        var key = nextEl.getAttribute('data-line-item-key');
        var existing = key ? currByKey[key] : null;
        if (existing) {
          var currDetails = existing.querySelector('.oddit-cart-drawer__item-details');
          var nextDetails = nextEl.querySelector('.oddit-cart-drawer__item-details');
          if (currDetails && nextDetails) {
            currDetails.innerHTML = nextDetails.innerHTML;
          }
          frag.appendChild(existing);
          delete currByKey[key];
        } else {
          frag.appendChild(nextEl.cloneNode(true));
        }
      }
    );

    currContainer.innerHTML = '';
    currContainer.appendChild(frag);
  }

  function applyRenderedSection(sectionEl, html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var newSection = doc.querySelector(SECTION_SELECTOR);
    if (!newSection) return;

    var newIsEmpty = newSection.classList.contains(EMPTY_CLASS);
    sectionEl.classList.toggle(EMPTY_CLASS, newIsEmpty);

    syncText(sectionEl, newSection, '[data-cart-total-items]');
    syncCartItems(sectionEl, newSection);
    syncText(sectionEl, newSection, '[data-cart-subtotal]');

    var currShipBar = sectionEl.querySelector('.oddit-cart-drawer__shipping-bar');
    var nextShipBar = newSection.querySelector('.oddit-cart-drawer__shipping-bar');
    if (currShipBar && nextShipBar) currShipBar.innerHTML = nextShipBar.innerHTML;

    syncInnerHTML(sectionEl, newSection, '[data-cart-empty]');
    syncInnerHTML(sectionEl, newSection, '[data-cross-sell-products]');
  }

  function updateHeaderBadges(cart) {
    var count = cart.item_count || 0;
    var badgeEls = document.querySelectorAll('[data-cart-count]');
    badgeEls.forEach(function (el) {
      el.textContent = String(count);
    });
  }

  function filterUpsells(cart) {
    var productIds = (cart.items || []).map(function (item) {
      return String(item.product_id);
    });
    sections.forEach(function (section) {
      var cards = section.querySelectorAll('[data-product-id]');
      cards.forEach(function (card) {
        var pid = String(card.getAttribute('data-product-id'));
        if (productIds.indexOf(pid) !== -1) {
          card.setAttribute('hidden', '');
          card.style.display = 'none';
        } else {
          card.removeAttribute('hidden');
          card.style.display = '';
        }
      });
    });
  }

  function renderCrossSell(cart) {
    sections.forEach(function (section) {
      var container = section.querySelector('[data-cross-sell-container]');
      if (!container) return;
      if (container.getAttribute('data-cross-sell-source') === 'manual') return;

      var intent = container.getAttribute('data-cross-sell-intent') || 'complementary';
      var limit = parseInt(container.getAttribute('data-cross-sell-limit'), 10) || 4;
      var productsContainer = container.querySelector('[data-cross-sell-products]');
      if (!productsContainer) return;

      var cartProductIds = (cart.items || []).map(function (item) {
        return item.product_id;
      });

      if (cartProductIds.length === 0) {
        productsContainer.innerHTML = '';
        return;
      }

      var referenceProductId = cartProductIds[cartProductIds.length - 1];
      var sectionId = section.id;
      var url = '/recommendations/products'
        + '?section_id=' + encodeURIComponent(sectionId)
        + '&product_id=' + encodeURIComponent(referenceProductId)
        + '&limit=' + encodeURIComponent(limit)
        + '&intent=' + encodeURIComponent(intent);

      fetch(url)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var freshProducts = doc.querySelector('[data-cross-sell-products]');
          if (freshProducts) {
            productsContainer.innerHTML = freshProducts.innerHTML;
            filterUpsells(cartState || cart);
          }
        })
        .catch(function (err) {
          console.error('[oddit-cart-drawer] Cross-sell fetch failed:', err);
        });
    });
  }

  function updateProgressBar(cart) {
    sections.forEach(function (section) {
      var threshold = parseFloat(section.getAttribute('data-free-shipping-threshold'));
      if (!threshold || threshold <= 0) return;

      var itemCount = cart.item_count || 0;
      var percentage = Math.min((itemCount / threshold) * 100, 100);
      var remaining = Math.max(threshold - itemCount, 0);

      var fills = section.querySelectorAll('.oddit-cart-drawer__shipping-fill');
      fills.forEach(function (el) {
        el.style.width = percentage + '%';
      });

      var texts = section.querySelectorAll('.oddit-cart-drawer__shipping-text, [data-cart-progress-label]');
      texts.forEach(function (el) {
        if (remaining <= 0) {
          el.innerHTML = 'You qualify for <strong>FREE shipping</strong>!';
        } else {
          el.innerHTML = 'Add <strong>' + remaining + ' more bottle' + (remaining !== 1 ? 's' : '') + '</strong> to get <strong>FREE shipping</strong>';
        }
      });
    });
  }

  function renderCart(cart) {
    if (!cart) return;
    cartState = cart;

    if (cart.currency) {
      activeCurrency = cart.currency;
    }

    if (cart.sections && typeof cart.sections === 'object') {
      sections.forEach(function (section) {
        var html = cart.sections[section.id];
        if (html) applyRenderedSection(section, html);
      });
    }

    var count = cart.item_count || 0;
    sections.forEach(function (section) {
      section.classList.toggle(EMPTY_CLASS, count === 0);

      var filled = section.querySelector('[data-cart-filled]');
      var empty = section.querySelector('[data-cart-empty]');
      var footer = section.querySelector('[data-cart-footer-filled]');
      if (filled) filled.style.display = count > 0 ? '' : 'none';
      if (empty) empty.style.display = count === 0 ? '' : 'none';
      if (footer) footer.style.display = count > 0 ? '' : 'none';
    });

    if (!(cart.sections && typeof cart.sections === 'object')) {
      updateProgressBar(cart);
    }
    updateHeaderBadges(cart);
    filterUpsells(cart);
    renderCrossSell(cart);
    dispatchCartUpdated(cart);
  }

  /* ------------------------------------------------------------------ *
   * Per-section initialization (UI shell)
   * ------------------------------------------------------------------ */

  function initSection(section) {
    if (!section || section.dataset.odditCartDrawerInit === 'true') return;
    section.dataset.odditCartDrawerInit = 'true';
    registerSection(section);

    if (section.style.display === 'none') section.style.display = '';

    var panel = section.querySelector('.oddit-cart-drawer__panel');
    var itemsContainer = section.querySelector('[data-cart-items]');

    var abortController = new AbortController();
    var signal = abortController.signal;
    section.__odditAbortController = abortController;

    var lastFocused = null;

    function getFocusable() {
      if (!panel) return [];
      return Array.prototype.slice
        .call(panel.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(function (el) {
          return !el.hasAttribute('disabled') &&
            el.offsetParent !== null &&
            el.getAttribute('aria-hidden') !== 'true';
        });
    }

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    function open() {
      if (section.classList.contains(OPEN_CLASS)) return;
      lastFocused = document.activeElement;
      section.classList.add(OPEN_CLASS);
      document.body.style.overflow = 'hidden';
      refreshCart().catch(function () {});
      window.requestAnimationFrame(function () {
        var closeBtn = section.querySelector('[data-cart-drawer-close]');
        var target = (closeBtn && !closeBtn.disabled && closeBtn) || getFocusable()[0] || panel;
        if (target && typeof target.focus === 'function') target.focus();
      });
    }

    function close() {
      if (!section.classList.contains(OPEN_CLASS)) return;
      section.classList.remove(OPEN_CLASS);
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus(); } catch (err) {}
      }
    }

    function toggle() {
      if (section.classList.contains(OPEN_CLASS)) close(); else open();
    }

    /* ---------- close triggers ---------- */

    section.querySelectorAll('[data-cart-drawer-close]').forEach(function (el) {
      el.addEventListener('click', close, { signal: signal });
    });

    document.addEventListener('keydown', function (e) {
      if (!section.classList.contains(OPEN_CLASS)) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'Tab') trapFocus(e);
    }, { signal: signal });

    /* ---------- event delegation: quantity + remove (line items) ---------- */

    if (itemsContainer) {
      itemsContainer.addEventListener('click', function (e) {
        var quantityBtn = e.target.closest('[data-quantity-change]');
        var removeBtn = e.target.closest('[data-remove-item]');

        var actionBtn = quantityBtn || removeBtn;
        if (!actionBtn) return;

        var lineItem = actionBtn.closest('[data-line-item]');
        if (lineItem && lineItem.classList.contains('is-busy')) return;

        var lineKey =
          actionBtn.getAttribute('data-line-item-key') ||
          (lineItem && lineItem.getAttribute('data-line-item-key'));
        if (!lineKey) return;

        e.preventDefault();
        if (lineItem) lineItem.classList.add('is-busy');

        var promise;
        if (removeBtn) {
          promise = removeFromCart(lineKey);
        } else {
          var valueEl = lineItem
            ? lineItem.querySelector('[data-quantity-input]')
            : null;
          var current = valueEl ? parseInt(valueEl.textContent, 10) || 0 : 0;
          var delta = parseInt(quantityBtn.getAttribute('data-quantity-change'), 10) || 0;
          var next = current + delta;
          if (next < 0) next = 0;
          promise = updateLineItemQuantity(lineKey, next);
        }

        promise
          .catch(function (err) {
            console.error('[oddit-cart-drawer]', err);
          })
          .finally(function () {
            if (lineItem) lineItem.classList.remove('is-busy');
          });
      }, { signal: signal });
    }

    /* ---------- upsell / cross-sell / add-to-cart / subscription switch ---------- */

    section.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-additional-checkout]')) return;

      var switchBtn = e.target.closest(
        '.oddit-cart-drawer__switch-cta[data-selling-plan-id], [data-switch-subscription][data-selling-plan-id]'
      );
      if (switchBtn) {
        e.preventDefault();
        var planId = switchBtn.getAttribute('data-selling-plan-id');
        var explicitKey = switchBtn.getAttribute('data-line-key') || switchBtn.getAttribute('data-line-item-key');
        var keys = [];
        if (explicitKey) {
          keys = [explicitKey];
        } else if (cartState && cartState.items) {
          keys = cartState.items
            .filter(function (item) { return !item.selling_plan_allocation; })
            .map(function (item) { return item.key; });
        }
        if (!keys.length || !planId) return;

        switchBtn.disabled = true;
        var chain = Promise.resolve();
        keys.forEach(function (k) {
          chain = chain.then(function () {
            return updateLineItemToSubscription(k, planId);
          });
        });
        chain
          .catch(function (err) { console.error('[oddit-cart-drawer]', err); })
          .finally(function () { switchBtn.disabled = false; });
        return;
      }

      var addBtn = e.target.closest(
        '[data-add-to-cart][data-variant-id], ' +
        '.oddit-cart-drawer__upsell-cta[data-variant-id], ' +
        '.oddit-cart-drawer__upsell-card-add[data-variant-id], ' +
        '.oddit-cart-drawer__bundle-add[data-variant-id], ' +
        '.oddit-cart-drawer__rec-add[data-variant-id], ' +
        '.oddit-cart-drawer__cross-sell-add[data-variant-id]'
      );
      if (!addBtn) return;

      var variantId = Number(addBtn.getAttribute('data-variant-id'));
      if (!variantId) return;

      e.preventDefault();
      var restWidth = addBtn.getBoundingClientRect().width;
      addBtn.style.minWidth = restWidth + 'px';
      addBtn.disabled = true;
      addBtn.classList.add('is-loading');
      addBtn.setAttribute('aria-busy', 'true');

      var quantity = Number(addBtn.getAttribute('data-quantity')) || 1;
      addToCart(variantId, quantity)
        .then(function () { open(); })
        .catch(function (err) { console.error('[oddit-cart-drawer]', err); })
        .finally(function () {
          addBtn.disabled = false;
          addBtn.classList.remove('is-loading');
          addBtn.removeAttribute('aria-busy');
          addBtn.style.minWidth = '';
        });
    }, { signal: signal });

    initGift(section, signal);

    section.__odditOpen = open;
    section.__odditClose = close;
    section.__odditToggle = toggle;
  }

  /* ------------------------------------------------------------------ *
   * Gifting (cart attribute + cart note)
   * ------------------------------------------------------------------ */

  function initGift(section, signal) {
    var gift = section.querySelector('[data-cart-gift]');
    if (!gift) return;

    var checkbox = gift.querySelector('[data-cart-gift-checkbox]');
    var messageWrap = gift.querySelector('[data-cart-gift-message]');
    var note = gift.querySelector('[data-cart-gift-note]');
    var checkoutLink = section.querySelector('.oddit-cart-drawer__checkout-cta');
    var timer = null;
    var pending = null;

    function save() {
      clearTimeout(timer);
      timer = null;
      var isGift = checkbox.checked;
      var request = jsonFetch('/cart/update.js', {
        method: 'POST',
        body: {
          attributes: { Gift: isGift ? 'Yes' : '' },
          note: isGift && note ? note.value.trim() : ''
        }
      })
        .then(function (cart) {
          cartState = cart;
          return cart;
        })
        .catch(function (err) { console.error('[oddit-cart-drawer]', err); })
        .finally(function () {
          if (pending === request) pending = null;
        });
      pending = request;
      return request;
    }

    function scheduleSave() {
      clearTimeout(timer);
      timer = setTimeout(save, 500);
    }

    checkbox.addEventListener('change', function () {
      if (messageWrap) messageWrap.hidden = !checkbox.checked;
      if (checkbox.checked && note) note.focus();
      save();
    }, { signal: signal });

    if (note) {
      note.addEventListener('input', scheduleSave, { signal: signal });
      note.addEventListener('blur', function () {
        if (timer) save();
      }, { signal: signal });
    }

    if (checkoutLink) {
      checkoutLink.addEventListener('click', function (e) {
        if (!timer && !pending) return;
        e.preventDefault();
        var href = checkoutLink.getAttribute('href');
        (timer ? save() : pending).then(function () {
          window.location.href = href;
        });
      }, { signal: signal });
    }
  }

  function destroySection(section) {
    if (!section) return;
    if (section.__odditAbortController) {
      section.__odditAbortController.abort();
      section.__odditAbortController = null;
    }
    delete section.dataset.odditCartDrawerInit;
    unregisterSection(section);
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
    if (sections.length) {
      refreshCart().catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) {
      initSection(section);
      refreshCart().catch(function () {});
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });

  /* ------------------------------------------------------------------ *
   * Public API
   * ------------------------------------------------------------------ */

  window.OdditCartDrawer = {
    open: function () {
      var target = sections[sections.length - 1];
      if (target && typeof target.__odditOpen === 'function') target.__odditOpen();
    },
    close: function () {
      for (var i = 0; i < sections.length; i++) {
        if (typeof sections[i].__odditClose === 'function') sections[i].__odditClose();
      }
    },
    toggle: function () {
      var target = sections[sections.length - 1];
      if (target && typeof target.__odditToggle === 'function') target.__odditToggle();
    },
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart,
    formatMoney: formatMoney
  };

  window.odditCartDrawer = window.OdditCartDrawer;

  /* ------------------------------------------------------------------ *
   * Theme editor: preserve open state across section reloads
   * ------------------------------------------------------------------ */

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    var STATE_KEY = 'oddit-cart-drawer-editor-open';

    function findDrawerIn(node) {
      if (!node || typeof node.querySelector !== 'function') return null;
      if (node.matches && node.matches('[data-cart-drawer]')) return node;
      return node.querySelector('[data-cart-drawer]');
    }

    document.addEventListener('shopify:section:unload', function (event) {
      var drawer = findDrawerIn(event.target);
      if (!drawer) return;
      try {
        sessionStorage.setItem(STATE_KEY, drawer.classList.contains(OPEN_CLASS) ? '1' : '0');
      } catch (e) {}
    });

    document.addEventListener('shopify:section:load', function (event) {
      var drawer = findDrawerIn(event.target);
      if (!drawer) return;
      var wasOpen;
      try { wasOpen = sessionStorage.getItem(STATE_KEY) === '1'; } catch (e) { wasOpen = false; }
      if (!wasOpen) return;
      requestAnimationFrame(function () {
        if (window.OdditCartDrawer && typeof window.OdditCartDrawer.open === 'function') {
          window.OdditCartDrawer.open();
        }
      });
    });

    document.addEventListener('shopify:section:select', function (event) {
      var drawer = findDrawerIn(event.target);
      if (!drawer) return;
      if (window.OdditCartDrawer) window.OdditCartDrawer.open();
    });

    document.addEventListener('shopify:block:select', function (event) {
      var sectionEl = event.target && event.target.closest && event.target.closest('.shopify-section');
      var drawer = findDrawerIn(sectionEl) || findDrawerIn(event.target);
      if (!drawer) return;
      if (window.OdditCartDrawer) window.OdditCartDrawer.open();
    });
  }
})();